<script lang="ts">
    import ImgixImage from './ImgixImage.svelte';
    import Limiter from '../_wrappers/Limiter.svelte';
    import {
        maxWidth,
        maxWidthDesktop,
        maxWidthAnswer,
        maxWidthDesktopAnswer,
        desktopBreakpoint,
        imgixAr,
    } from '../_utils/constants';
    import { getParamsFromUrl, getRatioBySize } from '../_utils/helpers';
    import Transition from '../_wrappers/Transition.svelte';
    import clsx from 'clsx';

    // Types
    import type { TransitionEffect } from '$lib/types/Theme';
    import type { ImageSize } from '$lib/types/Components/SharedTypes';

    export let src = '';
    export let size: ImageSize = 'medium';
    export let framed = false;
    export let roundEdges: boolean;
    export let lazy = true;
    export let delayed = false;
    export let bgColor = 'transparent';
    export let duration = 200;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let fixedMaxWidth: number;
    export let sizes: string | undefined = undefined;

    let ratio: number | null | undefined;
    let imageWrapperClasses: string;

    const existingParams = getParamsFromUrl(src);

    const imgixParams = {
        ...existingParams,
        q: 80,
        dpr: 2,
        fit: 'crop',
        auto: ['format', 'compress'],
        ...(size !== 'custom' ? { ar: imgixAr[size] } : {}),
        ...(fixedMaxWidth && size !== 'custom' ? { w: fixedMaxWidth, h: undefined } : {}),
        ...(fixedMaxWidth && size === 'custom' && existingParams?.crop === 'faces,entropy'
            ? { w: fixedMaxWidth, h: undefined }
            : {}),
    };

    const getSizes = (size: ImageSize) => {
        let mobileWidth = maxWidth;
        let desktopWidth = maxWidthDesktop;

        if (size === 'answer') {
            mobileWidth = maxWidthAnswer;
            desktopWidth = maxWidthDesktopAnswer;
        }

        if (fixedMaxWidth && size === 'listItem') {
            mobileWidth = fixedMaxWidth;
            desktopWidth = fixedMaxWidth;
        }

        return `(max-width: ${desktopBreakpoint - 1}px) ${mobileWidth}px, ${desktopWidth}px`;
    };

    $: sizes = getSizes(size);
    $: ratio = getRatioBySize({ height: imgixParams.h, width: imgixParams.w, size });
    $: imageWrapperClasses = clsx('imageWrapper overflow-hidden leading-[0]', {
        'rounded-md md:rounded-lg': roundEdges || framed,
        'rounded-none': typeof roundEdges === 'boolean' && !roundEdges,
        'rounded-none md:rounded-lg': roundEdges === undefined && !framed,
    });
</script>

<div class="h-full w-full bg-no-repeat" style="background: {bgColor}">
    <Limiter wide={!framed} medium={framed}>
        <Transition delay={transitionDelay} effect={transitionEffect}>
            <div class={framed ? 'p-6 sm:px-12 sm:py-6 md:px-20 md:py-10' : 'p-0'}>
                <div class={imageWrapperClasses}>
                    <ImgixImage
                        {src}
                        ratio={ratio ? `${ratio}%` : null}
                        {imgixParams}
                        {sizes}
                        {lazy}
                        {duration}
                        {delayed}
                    />
                </div>
            </div>
        </Transition>
    </Limiter>
</div>
